import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { MouseEvent, ChangeEvent, useMemo, Fragment } from 'react';
import { Divider, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { InvoiceService } from 'api';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { format } from 'date-fns';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { SortDirection } from 'types/invoices';
import { isInvoiceCorrective } from 'utils/invoiceFunctions.utils';
import { numberWithCommas } from 'utils/numberFunctions.utils';
import { ClientFilters } from '../filters';
import {
  invoiceStatus,
  headCells,
  sortProperties,
} from './invoiceClientTable.const';
import {
  InvoicesTableProps,
  HeadCell,
  TableData,
  SortProperties,
} from './invoicesClientTable.types';

function createTableData({
  saleDate,
  issueDate,
  dueDate,
  code,
  id,
  ...invoiceData
}: Invoice | CorrectiveInvoice): TableData {
  return {
    id,
    code,
    jobCode: invoiceData.createdFrom?.code,
    saleDate: saleDate ? format(saleDate, DISPLAY_DATE_FORMAT) : '',
    issueDate: issueDate ? format(issueDate, DISPLAY_DATE_FORMAT) : '',
    dueDate: dueDate ? format(dueDate, DISPLAY_DATE_FORMAT) : '',
    grossValue: `${numberWithCommas(invoiceData.totals.grossValue)} ${
      invoiceData.currencyCode.currency
    }`,
    status: invoiceData.paid ? invoiceStatus.PAID : invoiceStatus.NOT_PAID,
    isCorrective: isInvoiceCorrective(invoiceData.invoiceClass),
  };
}
interface EnhancedTableProps {
  order?: SortDirection;
  orderBy?: string;
  sortProperties: SortProperties;
  cells: HeadCell[];
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof TableData,
  ) => void;
}

function EnhancedTableHead({
  order,
  orderBy,
  onRequestSort,
  cells,
  sortProperties,
}: EnhancedTableProps) {
  const createSortHandler =
    (property: keyof TableData) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {cells.map((headCell) => {
          const isSortActive = orderBy === sortProperties[headCell.id];
          return (
            <StyledTableHeadCell
              key={headCell.id}
              align={headCell.align}
              padding='normal'
              sortDirection={isSortActive ? order : false}
            >
              <TableSortLabel
                active={isSortActive}
                direction={order}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === SortDirection.DESC
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </StyledTableHeadCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export function InvoicesClientTable({
  data,
  params,
  setParams,
  invoicesLoading,
}: InvoicesTableProps) {
  const { t } = useTranslation(['misc', 'datagrid']);

  const { printInvoice } = InvoiceService.usePrintInvoice();
  const { sortProperty, sortDirection } = params;

  const rows = useMemo(() => data.invoices.map(createTableData), [data]);

  const handleRequestSort = (
    _: MouseEvent<unknown>,
    property: keyof TableData,
  ) => {
    const sortProperty = sortProperties[property];
    const isAsc = sortDirection === SortDirection.ASC;
    setParams((prev) => ({
      ...prev,
      sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
      sortProperty,
    }));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setParams((prev) => ({
      ...prev,
      resultSize: parseInt(event.target.value, 10),
      pageNumber: 1,
    }));
  };

  return (
    <Box sx={{ width: '85%', minWidth: '600px', maxWidth: '1824px' }}>
      <Paper sx={{ width: '100%', mb: 1.5 }}>
        <ClientFilters params={params} setParams={setParams} />
        <Divider />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby='tableTitle'
            size='medium'
          >
            <EnhancedTableHead
              order={sortDirection}
              orderBy={sortProperty}
              sortProperties={sortProperties}
              onRequestSort={handleRequestSort}
              cells={headCells}
            />
            <TableBody>
              {rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    key={row.id}
                    hover
                    tabIndex={-1}
                    sx={{ cursor: 'pointer', height: '48px' }}
                  >
                    {headCells.map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        align={cell.align || 'left'}
                        height='condensed'
                        onClick={() => printInvoice(row.id, row.isCorrective)}
                      >
                        {!invoicesLoading ? row[cell.id] : <Skeleton />}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component='div'
          count={data.count}
          rowsPerPage={params.resultSize}
          page={params.pageNumber - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            `${from} - ${to} ${t('of', { ns: 'datagrid' })} ${count}`
          }
          labelRowsPerPage={t('entriesPerPage', { ns: 'datagrid' })}
        />
      </Paper>
    </Box>
  );
}
